import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode, reload } from 'firebase/auth';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import './VerifyEmail.css';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      // Try multiple parameter names that Firebase might use
      const mode = searchParams.get('mode') || searchParams.get('action');
      const actionCode = searchParams.get('oobCode') || searchParams.get('oobcode') || searchParams.get('code');
      const continueUrl = searchParams.get('continueUrl') || searchParams.get('continue') || '/donate-anonymously';
      const apiKey = searchParams.get('apiKey');

      console.log('🔗 Email Verification Link Opened');
      console.log('  All params:', Object.fromEntries(searchParams.entries()));
      console.log('  Mode:', mode);
      console.log('  Action Code:', actionCode ? actionCode.substring(0, 8) + '...' : 'Missing');
      console.log('  API Key:', apiKey ? 'Present' : 'Missing');

      // Validate parameters - Firebase email verification links should have mode=verifyEmail
      if (!mode || mode !== 'verifyEmail') {
        setStatus('error');
        setError('Invalid verification link. The link may be from an old email. Please request a new verification email from your account settings.');
        return;
      }

      if (!actionCode) {
        setStatus('error');
        setError('Invalid verification link. Missing verification code. Please request a new verification email.');
        return;
      }

      try {
        console.log('🔄 Starting email verification...');
        
        // Apply the email verification action code
        // This works even if the user is not logged in
        await applyActionCode(auth, actionCode);
        console.log('✅ Email verification code applied successfully');

        // Try to get the current user (might be null if not logged in)
        let currentUser = auth.currentUser;
        
        if (currentUser) {
          console.log('🔄 User is logged in. Reloading to get updated verification status...');
          await reload(currentUser);
          console.log('✅ User reloaded. Email verified:', currentUser.emailVerified);

          // Update Firestore document
          if (currentUser.uid) {
            console.log('📝 Updating Firestore document...');
            try {
              const userDocRef = doc(db, 'users', currentUser.uid);
              await updateDoc(userDocRef, {
                emailVerified: true,
                updatedAt: serverTimestamp()
              });
              console.log('✅ Firestore document updated');
            } catch (firestoreError: any) {
              console.warn('⚠️ Could not update Firestore (user might not exist yet):', firestoreError);
              // Don't fail the verification if Firestore update fails
            }
          }
        } else {
          console.log('ℹ️ User is not logged in. Email is verified in Firebase Auth.');
          console.log('   User should log in to see the updated verification status.');
        }

        setStatus('success');
        console.log('✅ Email verification successful');

        // Redirect after 2 seconds
        setTimeout(() => {
          if (currentUser) {
            navigate(continueUrl === '/donate' ? '/donate-anonymously' : continueUrl);
          } else {
            navigate('/login');
          }
        }, 2000);
      } catch (err: any) {
        console.error('❌ Email verification error:', err);
        console.error('  Error code:', err.code);
        console.error('  Error message:', err.message);
        setStatus('error');
        
        // Handle specific error cases
        if (err.code === 'auth/expired-action-code') {
          setError('The verification link has expired. Verification links are valid for 3 days. Please request a new verification email.');
        } else if (err.code === 'auth/invalid-action-code') {
          setError('The verification link is invalid or has already been used. Please request a new verification email from your account settings.');
        } else if (err.code === 'auth/user-disabled') {
          setError('This account has been disabled. Please contact support for assistance.');
        } else if (err.code === 'auth/network-request-failed') {
          setError('Network error. Please check your internet connection and try again.');
        } else {
          setError(
            err.message || 
            'An error occurred during email verification. Please try again or request a new verification email.'
          );
        }
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className={`verify-email-page ${status === 'success' ? 'success-background' : ''}`}>
      <div className={`verify-email-container ${status === 'success' ? 'success-container' : ''}`}>
        {status === 'loading' && (
          <div className="verify-status loading">
            <div className="status-icon loading-spinner">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                  <animate attributeName="stroke-dasharray" dur="2s" values="0 32;16 16;0 32;0 32" repeatCount="indefinite"/>
                  <animate attributeName="stroke-dashoffset" dur="2s" values="0;-16;-32;-32" repeatCount="indefinite"/>
                </circle>
              </svg>
            </div>
            <h2>Verifying Your Email...</h2>
            <p>Please wait while we verify your email address.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="verify-status success">
            <div className="status-icon success-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2>Email Verified</h2>
            <p className="redirect-message">Redirecting you now...</p>
          </div>
        )}

        {status === 'error' && (
          <div className="verify-status error">
            <div className="status-icon error-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h2>Verification Failed</h2>
            <p className="error-message">{error}</p>
            <div className="error-help-text">
              <p><strong>What to do:</strong></p>
              <ul>
                <li>If this link is from an old email, please log in and request a new verification email</li>
                <li>Make sure you're using the most recent verification email (links expire after 3 days)</li>
                <li>If the problem persists, contact support</li>
              </ul>
            </div>
            <div className="error-actions">
              <button 
                className="btn-request-new"
                onClick={() => navigate('/login')}
              >
                Go to Login
              </button>
              <button 
                className="btn-go-home"
                onClick={() => navigate('/')}
              >
                Go to Home
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;

